import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@common/components/ui/form"
import { Input } from "@common/components/ui/input"
import { Label } from "@common/components/ui/label"
import { Switch } from "@common/components/ui/switch"
import { cn } from "@common/lib/utils"
import type { LucideIcon } from "lucide-react"
import { type HTMLInputTypeAttribute, useRef, useState } from "react"
import { type FieldValues, type Path, useFormContext } from "react-hook-form"
import { BareSelectField } from "./select"

const ROW = cn(
  "grid grid-cols-[7rem_minmax(0,1fr)] items-start gap-x-2 space-y-0",
)

export const TEXT = "text-small-regular"

const LABEL = cn(TEXT, "py-1.5 text-muted-foreground")

export const DISPLAY = cn(
  "flex h-7 w-full items-center rounded-md px-2 text-left",
  TEXT,
  "group hover:bg-muted disabled:cursor-default disabled:hover:bg-transparent",
)

export const PROMPT = "group-disabled:hidden"

export const INPUT = cn("px-2", TEXT)

export const SUFFIX = cn("shrink-0 text-muted-foreground", TEXT)

const ICON = "size-4 shrink-0 text-muted-foreground"

export const INLINE_TRIGGER = cn(
  "h-7 border-transparent bg-transparent px-2 text-left shadow-none",
  TEXT,
  "hover:bg-muted focus:ring-0 disabled:cursor-default disabled:opacity-100",
  "[&>svg]:opacity-0 hover:[&>svg]:opacity-50 focus-visible:[&>svg]:opacity-50",
)

export function InlineRow({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}): React.ReactElement {
  return (
    <div className={ROW}>
      <Label className={LABEL}>{label}</Label>
      <div className="min-w-0">{children}</div>
    </div>
  )
}

export function WithIcon({
  icon: Icon,
  children,
}: {
  icon?: LucideIcon
  children: React.ReactNode
}): React.ReactNode {
  return Icon ? (
    <div className="flex min-w-0 items-center gap-2">
      <Icon className={ICON} />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  ) : (
    children
  )
}

export function InlineTextField<T extends FieldValues>({
  name,
  label,
  type = "text",
  prefix = "",
  suffix = "",
  emptyLabel,
  disabled,
  display,
  bare = false,
  align = "left",
  icon,
}: {
  name: Path<T>
  label: string
  type?: HTMLInputTypeAttribute
  prefix?: string
  suffix?: string
  emptyLabel?: string
  disabled?: boolean
  display?: (value: string) => string
  bare?: boolean
  align?: "left" | "right"
  icon?: LucideIcon
}): React.ReactElement {
  const { control } = useFormContext<T>()
  const [editing, setEditing] = useState(false)
  const valueBeforeEdit = useRef("")
  const noun = label === label.toUpperCase() ? label : label.toLowerCase()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const value = String(field.value ?? "")

        return (
          <FormItem className={bare ? "space-y-0" : ROW}>
            <FormLabel className={bare ? "sr-only" : LABEL}>{label}</FormLabel>
            <div className="min-w-0">
              <WithIcon icon={icon}>
                {editing ? (
                  <div className="flex items-center gap-1">
                    {prefix && <span className={TEXT}>{prefix}</span>}
                    <FormControl>
                      <Input
                        size="sm"
                        {...field}
                        value={value}
                        type={type}
                        autoFocus
                        className={cn(INPUT, align === "right" && "text-right")}
                        onBlur={() => {
                          field.onBlur()
                          setEditing(false)
                        }}
                        onKeyDown={(event) => {
                          if (event.key === "Escape") {
                            field.onChange(valueBeforeEdit.current)
                          }
                          if (event.key === "Enter" || event.key === "Escape") {
                            event.preventDefault()
                            event.currentTarget.blur()
                          }
                        }}
                      />
                    </FormControl>
                    {suffix && <span className={SUFFIX}>{suffix}</span>}
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={disabled}
                    className={cn(
                      DISPLAY,
                      align === "right" && "justify-end",
                      fieldState.invalid && "text-destructive",
                    )}
                    onClick={() => {
                      valueBeforeEdit.current = value
                      setEditing(true)
                    }}
                  >
                    <span className="truncate">
                      {value ? (
                        `${prefix}${display ? display(value) : value}`
                      ) : (
                        <span
                          className={cn(
                            !fieldState.invalid && "text-muted-foreground",
                          )}
                        >
                          <span className={PROMPT}>
                            {emptyLabel ?? `Add ${noun}`}
                          </span>
                        </span>
                      )}
                    </span>
                    {value && suffix && (
                      <span className={cn(SUFFIX, "ml-1")}>{suffix}</span>
                    )}
                  </button>
                )}
              </WithIcon>
              <FormMessage className={cn(bare && "sr-only")} />
            </div>
          </FormItem>
        )
      }}
    />
  )
}

export function InlineSelectField<T extends FieldValues>({
  name,
  label,
  options,
  emptyLabel,
  icon,
  disabled,
}: {
  name: Path<T>
  label: string
  options: ReadonlyArray<{ value: string; label: string }>
  emptyLabel: string
  icon?: LucideIcon
  disabled?: boolean
}): React.ReactElement {
  return (
    <InlineRow label={label}>
      <WithIcon icon={icon}>
        <BareSelectField<T, string>
          name={name}
          options={options}
          placeholder={emptyLabel}
          emptyLabel={emptyLabel}
          clearable
          disabled={disabled}
          className={INLINE_TRIGGER}
        />
      </WithIcon>
    </InlineRow>
  )
}

export function InlineSwitchField<T extends FieldValues>({
  name,
  label,
  disabled,
  title,
  description,
}: {
  name: Path<T>
  label: string
  disabled?: boolean
  title?: string
  description?: string
}): React.ReactElement {
  const { control } = useFormContext<T>()

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={ROW}>
          <FormLabel className={LABEL}>{label}</FormLabel>
          <div className="flex h-7 items-center gap-2.5 px-2" title={title}>
            <FormControl>
              <Switch
                checked={Boolean(field.value)}
                onCheckedChange={field.onChange}
                disabled={disabled}
              />
            </FormControl>
            {description && <span className={TEXT}>{description}</span>}
          </div>
        </FormItem>
      )}
    />
  )
}
